import { Navigate, useLocation } from "react-router-dom";
import type { ReactNode } from "react";
import { useAuth } from "../context/AuthContext";
import type { TeamRole } from "../lib/types";

interface Props {
  children: ReactNode;
  allowedRoles?: TeamRole[];
}

export default function ProtectedRoute({ children, allowedRoles }: Props) {
  const { loading, session, member, role, isSuperUser } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center text-slate-500">
        Loading…
      </div>
    );
  }

  // Not signed in → login
  if (!session) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Signed in but no team_roster match → unauthorized
  if (!member) {
    return <Navigate to="/unauthorized" replace />;
  }

  // IC onboarding gate — new ICs who haven't signed their agreement yet
  // must complete signing before accessing any portal page. Super users
  // (admin accounts) skip this gate. Jun 2, 2026.
  if (
    !isSuperUser &&
    member.onboarding_complete === false &&
    location.pathname !== "/ic-sign"
  ) {
    return <Navigate to="/ic-sign" replace />;
  }

  // Role gate — super users (Greg/Chad/Jennifer) bypass all role allowlists
  if (
    !isSuperUser &&
    allowedRoles &&
    role &&
    !allowedRoles.includes(role) &&
    !(member?.secondary_role != null && allowedRoles.includes(member.secondary_role))
  ) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
}
